import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  Button,
  Alert,
  Skeleton,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  LinearProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Category as CategoryIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Assessment as AssessmentIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

// Import existing components
import { DateRangePicker } from '../Financial/DateRangePicker';

// Import hooks
import { useCategoryUsageReports } from '../../hooks/api';

// Types
interface CategoryUsage {
  categoryId: string;
  categoryName: string;
  totalAmount: number;
  usageCount: number;
  hostCount: number;
  averageAmount: number;
  percentageOfTotal: number;
  topHosts: Array<{
    hostId: string;
    hostName: string;
    amount: number;
    count: number;
  }>;
}

interface CategoryTrend {
  categoryId: string;
  categoryName: string;
  monthlyUsage: Array<{
    month: string;
    amount: number;
    count: number;
  }>;
  trend: 'increasing' | 'decreasing' | 'stable';
}

interface CategoryUsageReport {
  totalCategories: number;
  totalUsage: number;
  categoryStats: CategoryUsage[];
  mostPopularCategories: CategoryUsage[];
  leastUsedCategories: CategoryUsage[];
  categoryTrends: CategoryTrend[];
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

interface CategoryUsageReportsProps {
  data?: CategoryUsageReport;
  loading?: boolean;
  error?: string;
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
  onDateRangeChange?: (dateRange: { startDate: Date; endDate: Date }) => void;
  onRefresh?: () => void;
  onExport?: (format: 'csv' | 'excel') => void;
}

type SortField = 'categoryName' | 'totalAmount' | 'usageCount' | 'hostCount' | 'averageAmount' | 'percentageOfTotal';
type SortDirection = 'asc' | 'desc';

const CategoryUsageReports: React.FC<CategoryUsageReportsProps> = memo(({
  data: propData,
  loading: propLoading = false,
  error: propError,
  dateRange,
  onDateRangeChange,
  onRefresh,
  onExport,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [viewMode, setViewMode] = useState<'overview' | 'detailed' | 'trends' | 'distribution'>('overview');
  const [sortField, setSortField] = useState<SortField>('totalAmount');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Use API hook for data fetching
  const {
    data: apiData,
    loading: apiLoading,
    error: apiError,
    refresh: apiRefresh,
    exportData: apiExport
  } = useCategoryUsageReports({
    dateRange,
    autoRefresh: false
  });

  // Use prop data if provided, otherwise use API data
  const data = propData || apiData;
  const loading = propLoading || apiLoading;
  const error = propError || apiError;

  // Mock data for development - replace with actual API data
  const mockData: CategoryUsageReport = useMemo(() => ({
    totalCategories: 10,
    totalUsage: 125000,
    categoryStats: [
      {
        categoryId: 'utilities',
        categoryName: 'Utilities',
        totalAmount: 35000,
        usageCount: 145,
        hostCount: 8,
        averageAmount: 241.38,
        percentageOfTotal: 28.0,
        topHosts: [
          { hostId: 'host-1', hostName: 'John Smith', amount: 12000, count: 48 },
          { hostId: 'host-2', hostName: 'Sarah Johnson', amount: 8500, count: 32 },
          { hostId: 'host-3', hostName: 'Mike Wilson', amount: 6200, count: 28 },
        ]
      },
      {
        categoryId: 'maintenance',
        categoryName: 'Maintenance',
        totalAmount: 28000,
        usageCount: 89,
        hostCount: 7,
        averageAmount: 314.61,
        percentageOfTotal: 22.4,
        topHosts: [
          { hostId: 'host-1', hostName: 'John Smith', amount: 9500, count: 28 },
          { hostId: 'host-4', hostName: 'Lisa Brown', amount: 7200, count: 22 },
          { hostId: 'host-2', hostName: 'Sarah Johnson', amount: 5800, count: 18 },
        ]
      },
      {
        categoryId: 'cleaning',
        categoryName: 'Cleaning',
        totalAmount: 22000,
        usageCount: 156,
        hostCount: 9,
        averageAmount: 141.03,
        percentageOfTotal: 17.6,
        topHosts: [
          { hostId: 'host-2', hostName: 'Sarah Johnson', amount: 6800, count: 42 },
          { hostId: 'host-3', hostName: 'Mike Wilson', amount: 5200, count: 38 },
          { hostId: 'host-1', hostName: 'John Smith', amount: 4500, count: 32 },
        ]
      },
      {
        categoryId: 'supplies',
        categoryName: 'Supplies',
        totalAmount: 18000,
        usageCount: 203,
        hostCount: 10,
        averageAmount: 88.67,
        percentageOfTotal: 14.4,
        topHosts: [
          { hostId: 'host-5', hostName: 'Tom Davis', amount: 4200, count: 48 },
          { hostId: 'host-2', hostName: 'Sarah Johnson', amount: 3800, count: 42 },
          { hostId: 'host-1', hostName: 'John Smith', amount: 3200, count: 38 },
        ]
      },
      {
        categoryId: 'insurance',
        categoryName: 'Insurance',
        totalAmount: 15000,
        usageCount: 24,
        hostCount: 6,
        averageAmount: 625.00,
        percentageOfTotal: 12.0,
        topHosts: [
          { hostId: 'host-1', hostName: 'John Smith', amount: 4500, count: 6 },
          { hostId: 'host-2', hostName: 'Sarah Johnson', amount: 3200, count: 4 },
          { hostId: 'host-4', hostName: 'Lisa Brown', amount: 2800, count: 4 },
        ]
      },
      {
        categoryId: 'marketing',
        categoryName: 'Marketing',
        totalAmount: 7000,
        usageCount: 32,
        hostCount: 5,
        averageAmount: 218.75,
        percentageOfTotal: 5.6,
        topHosts: [
          { hostId: 'host-3', hostName: 'Mike Wilson', amount: 2200, count: 8 },
          { hostId: 'host-1', hostName: 'John Smith', amount: 1800, count: 6 },
          { hostId: 'host-6', hostName: 'Anna White', amount: 1500, count: 7 },
        ]
      }
    ],
    mostPopularCategories: [],
    leastUsedCategories: [],
    categoryTrends: [
      {
        categoryId: 'utilities',
        categoryName: 'Utilities',
        monthlyUsage: [
          { month: '2024-01', amount: 8500, count: 35 },
          { month: '2024-02', amount: 9200, count: 38 },
          { month: '2024-03', amount: 10800, count: 42 },
        ],
        trend: 'increasing'
      },
      {
        categoryId: 'maintenance',
        categoryName: 'Maintenance',
        monthlyUsage: [
          { month: '2024-01', amount: 12000, count: 32 },
          { month: '2024-02', amount: 8500, count: 28 },
          { month: '2024-03', amount: 7500, count: 29 },
        ],
        trend: 'decreasing'
      },
      {
        categoryId: 'cleaning',
        categoryName: 'Cleaning',
        monthlyUsage: [
          { month: '2024-01', amount: 7200, count: 52 },
          { month: '2024-02', amount: 7400, count: 51 },
          { month: '2024-03', amount: 7400, count: 53 },
        ],
        trend: 'stable'
      }
    ],
    dateRange: {
      startDate: dateRange.startDate.toISOString(),
      endDate: dateRange.endDate.toISOString()
    }
  }), [dateRange]);

  const reportData = data || mockData;

  // Sorting logic
  const sortedCategories = useMemo(() => {
    const categories = [...reportData.categoryStats];
    return categories.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return sortDirection === 'asc' 
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });
  }, [reportData.categoryStats, sortField, sortDirection]);

  // Handle sort
  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  }, [sortField]);

  // Format currency
  const formatCurrency = useCallback((amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }, []);

  // Get trend icon
  const getTrendIcon = useCallback((trend: 'increasing' | 'decreasing' | 'stable') => {
    switch (trend) {
      case 'increasing':
        return <TrendingUpIcon color="success" fontSize="small" />;
      case 'decreasing':
        return <TrendingDownIcon color="error" fontSize="small" />;
      case 'stable':
        return <TrendingFlatIcon color="info" fontSize="small" />;
    }
  }, []);

  // Handle date range change
  const handleDateRangeChange = useCallback((newDateRange: { startDate: Date; endDate: Date }) => {
    onDateRangeChange?.(newDateRange);
  }, [onDateRangeChange]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    if (onRefresh) {
      onRefresh();
    } else {
      await apiRefresh();
    }
  }, [onRefresh, apiRefresh]);

  // Handle export
  const handleExport = useCallback(async (format: 'csv' | 'excel') => {
    if (onExport) {
      onExport(format);
    } else {
      await apiExport(format);
    }
  }, [onExport, apiExport]);

  // Render loading state
  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={40} sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Skeleton variant="rectangular" height={200} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        <Typography variant="h6">Error Loading Category Usage Reports</Typography>
        <Typography variant="body2">{error}</Typography>
        {onRefresh && (
          <Button onClick={onRefresh} sx={{ mt: 1 }}>
            Retry
          </Button>
        )}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <CategoryIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
              Expense Category Usage Reports
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Analysis of expense category utilization across all hosts
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Controls */}
      <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Report Controls
        </Typography>
        
        <Grid container spacing={3} alignItems="flex-end">
          {/* View Mode */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>View Mode</InputLabel>
              <Select
                value={viewMode}
                label="View Mode"
                onChange={(e) => setViewMode(e.target.value as any)}
              >
                <MenuItem value="overview">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <AssessmentIcon fontSize="small" />
                    <span>Overview</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="detailed">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <BarChartIcon fontSize="small" />
                    <span>Detailed Analysis</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="trends">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TrendingUpIcon fontSize="small" />
                    <span>Trends</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="distribution">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PieChartIcon fontSize="small" />
                    <span>Distribution</span>
                  </Stack>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Date Range Picker */}
          <Grid item xs={12} md={4}>
            <DateRangePicker
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
              presetOptions={[
                {
                  label: 'Last 30 days',
                  value: 'last_30_days',
                  startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
                  endDate: new Date()
                },
                {
                  label: 'Last 90 days',
                  value: 'last_90_days',
                  startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
                  endDate: new Date()
                },
                {
                  label: 'Last 12 months',
                  value: 'last_12_months',
                  startDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000),
                  endDate: new Date()
                }
              ]}
              loading={loading}
            />
          </Grid>

          {/* Actions */}
          <Grid item xs={12} md={5}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => handleExport('csv')}
                disabled={loading}
              >
                Export CSV
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => handleExport('excel')}
                disabled={loading}
              >
                Export Excel
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <CategoryIcon color="primary" />
                <Box>
                  <Typography variant="h6" color="primary">
                    {reportData.totalCategories}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Categories
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <AssessmentIcon color="secondary" />
                <Box>
                  <Typography variant="h6" color="secondary">
                    {formatCurrency(reportData.totalUsage)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Usage Amount
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {formatCurrency(reportData.totalUsage / reportData.totalCategories)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average per Category
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="info.main">
                {reportData.categoryStats.reduce((sum, cat) => sum + cat.usageCount, 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Transactions
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      {viewMode === 'overview' && (
        <Grid container spacing={3}>
          {/* Top Categories */}
          <Grid item xs={12} lg={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Category Usage Overview
                </Typography>
                <Stack spacing={3}>
                  {sortedCategories.slice(0, 6).map((category, index) => (
                    <Box key={category.categoryId}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="body1" fontWeight="medium">
                          {category.categoryName}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Typography variant="body2" color="text.secondary">
                            {category.hostCount} hosts
                          </Typography>
                          <Typography variant="body1" color="primary">
                            {formatCurrency(category.totalAmount)}
                          </Typography>
                        </Stack>
                      </Stack>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={category.percentageOfTotal}
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 45 }}>
                          {category.percentageOfTotal.toFixed(1)}%
                        </Typography>
                      </Box>
                      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {category.usageCount} transactions
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Avg: {formatCurrency(category.averageAmount)}
                        </Typography>
                      </Stack>
                      {index < 5 && <Divider sx={{ mt: 2 }} />}
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Category Trends */}
          <Grid item xs={12} lg={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Category Trends
                </Typography>
                <Stack spacing={2}>
                  {reportData.categoryTrends.slice(0, 8).map((trend) => (
                    <Box key={trend.categoryId}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2">
                          {trend.categoryName}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {getTrendIcon(trend.trend)}
                          <Typography variant="caption" color="text.secondary">
                            {trend.trend}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {viewMode === 'detailed' && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Detailed Category Analysis
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'categoryName'}
                        direction={sortField === 'categoryName' ? sortDirection : 'asc'}
                        onClick={() => handleSort('categoryName')}
                      >
                        Category
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortField === 'totalAmount'}
                        direction={sortField === 'totalAmount' ? sortDirection : 'asc'}
                        onClick={() => handleSort('totalAmount')}
                      >
                        Total Amount
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortField === 'usageCount'}
                        direction={sortField === 'usageCount' ? sortDirection : 'asc'}
                        onClick={() => handleSort('usageCount')}
                      >
                        Usage Count
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortField === 'hostCount'}
                        direction={sortField === 'hostCount' ? sortDirection : 'asc'}
                        onClick={() => handleSort('hostCount')}
                      >
                        Host Count
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortField === 'averageAmount'}
                        direction={sortField === 'averageAmount' ? sortDirection : 'asc'}
                        onClick={() => handleSort('averageAmount')}
                      >
                        Average Amount
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortField === 'percentageOfTotal'}
                        direction={sortField === 'percentageOfTotal' ? sortDirection : 'asc'}
                        onClick={() => handleSort('percentageOfTotal')}
                      >
                        % of Total
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">Top Hosts</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedCategories.map((category) => (
                    <TableRow key={category.categoryId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {category.categoryName}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="primary">
                          {formatCurrency(category.totalAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {category.usageCount.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {category.hostCount}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatCurrency(category.averageAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {category.percentageOfTotal.toFixed(1)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            <Box>
                              {category.topHosts.slice(0, 3).map((host) => (
                                <Typography key={host.hostId} variant="caption" display="block">
                                  {host.hostName}: {formatCurrency(host.amount)} ({host.count} transactions)
                                </Typography>
                              ))}
                            </Box>
                          }
                        >
                          <IconButton size="small">
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {viewMode === 'trends' && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Category Usage Trends
                </Typography>
                <Grid container spacing={3}>
                  {reportData.categoryTrends.map((trend) => (
                    <Grid item xs={12} md={6} lg={4} key={trend.categoryId}>
                      <Card variant="outlined">
                        <CardContent>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="h6">
                              {trend.categoryName}
                            </Typography>
                            {getTrendIcon(trend.trend)}
                          </Stack>
                          <Stack spacing={1}>
                            {trend.monthlyUsage.map((month) => (
                              <Box key={month.month} display="flex" justifyContent="space-between">
                                <Typography variant="body2" color="text.secondary">
                                  {new Date(month.month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}:
                                </Typography>
                                <Typography variant="body2">
                                  {formatCurrency(month.amount)}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                          <Chip
                            label={trend.trend}
                            size="small"
                            color={
                              trend.trend === 'increasing' ? 'success' :
                              trend.trend === 'decreasing' ? 'error' : 'info'
                            }
                            sx={{ mt: 2 }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {viewMode === 'distribution' && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Category Usage Distribution
                </Typography>
                <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Stack spacing={2} sx={{ width: '100%', maxWidth: 600 }}>
                    {sortedCategories.slice(0, 8).map((category, index) => (
                      <Box key={category.categoryId}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {category.categoryName}
                          </Typography>
                          <Typography variant="body2" color="primary">
                            {category.percentageOfTotal.toFixed(1)}%
                          </Typography>
                        </Stack>
                        <Box
                          sx={{
                            width: '100%',
                            height: 24,
                            backgroundColor: 'grey.200',
                            borderRadius: 1,
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          <Box
                            sx={{
                              width: `${category.percentageOfTotal}%`,
                              height: '100%',
                              backgroundColor: `hsl(${index * 45}, 70%, 50%)`,
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {formatCurrency(category.totalAmount)} • {category.usageCount} transactions
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Distribution Summary
                </Typography>
                <Stack spacing={2}>
                  {sortedCategories.slice(0, 6).map((category) => (
                    <Box key={category.categoryId}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2">
                          {category.categoryName}
                        </Typography>
                        <Box textAlign="right">
                          <Typography variant="body2" color="primary">
                            {category.percentageOfTotal.toFixed(1)}%
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatCurrency(category.totalAmount)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
});

CategoryUsageReports.displayName = 'CategoryUsageReports';

export default CategoryUsageReports;